import { Component, OnInit } from '@angular/core';
import { DeliveryMethod, CertificateStatus } from '../../../variables/constant';
import { CustomerFormModel, ReasonDeniedModel } from '../../../variables/form';
import { SortColumn, SortEvent, SortDirection } from '../../../services/sortable.directive';
import { FormService } from '../../../services/form.service';
import { PdfService } from '../../../services/pdf.services';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

function compare(a: any, b: any): number {
  // Handle null/undefined values
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;

  // Convert to string and trim whitespace for consistent comparison
  const aStr = String(a).trim().toLowerCase();
  const bStr = String(b).trim().toLowerCase();

  // Try to parse as numbers first
  const aNum = parseFloat(aStr);
  const bNum = parseFloat(bStr);
  
  if (!isNaN(aNum) && !isNaN(bNum)) {
    return aNum - bNum;
  }

  // Try to parse as dates
  const aDate = new Date(aStr);
  const bDate = new Date(bStr);
  
  if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
    return aDate.getTime() - bDate.getTime();
  }

  // Default string comparison
  return aStr.localeCompare(bStr);
}

// Enhanced sortData function with column-specific handling
function sortData(datalist: any[], column: string, direction: string): any[] {
  if (direction === '' || column === '') {
    return datalist;
  }

  return [...datalist].sort((a, b) => {
    let aValue = a[column];
    let bValue = b[column];

    // Column-specific preprocessing
    switch (column) {
      case 'dateSubmitted':
      case 'expirationDate':
        // Handle date columns - convert to Date objects for proper comparison
        aValue = aValue ? new Date(aValue) : null;
        bValue = bValue ? new Date(bValue) : null;
        break;
        
      case 'State':
        // Normalize state values (handle abbreviations and full names)
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
        break;
        
      case 'customerNumber':
        // Handle customer numbers that might be strings or numbers
        aValue = aValue ? parseFloat(String(aValue).replace(/[^\d.-]/g, '')) : null;
        bValue = bValue ? parseFloat(String(bValue).replace(/[^\d.-]/g, '')) : null;
        break;
        
      case 'validationStatus':
        // Define custom order for validation status
        aValue = getStatusOrder(aValue);
        bValue = getStatusOrder(bValue);
        break;
    }

    const res = compare(aValue, bValue);
    return direction === 'asc' ? res : -res;
  });
}

// Helper function to normalize state values
function normalizeState(state: string): string {
  if (!state) return '';
  
  const stateAbbr: { [key: string]: string } = {
    'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
    'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
    'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
    'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
    'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
    'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
    'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
    'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
    'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
    'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
    'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
    'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
    'wisconsin': 'WI', 'wyoming': 'WY'
  };
  
  const normalized = state.toLowerCase().trim();
  return stateAbbr[normalized] || state.toUpperCase().trim();
}

// Helper function to define custom order for validation status
function getStatusOrder(status: string): number {
  if (!status) return 999;
  
  const statusOrder: { [key: string]: number } = {
    'pending': 1,
    'in-progress': 2,
    'validated': 3,
    'approved': 4,
    'rejected': 5,
    'expired': 6
  };
  
  return statusOrder[status.toLowerCase()] || 999;
}


const approvePdf = async (formService: FormService, customerFormId: number, fileName: string): Promise<any> => {

  let response = await formService.approvePdf(customerFormId, fileName)
    .toPromise()
    .catch(error => {
      if (error instanceof Object) {
        if (error["ResponseException"] != "") {
          alert(error["ResponseException"]);
        }
      }
      alert("Something went wrong. Please take note of the time this happened and contact your administrator. Error:" + error);
      console.log(error);
      location.reload();
    });
  if (response instanceof Object) {
    if (response["message"] != "" && response["message"] != undefined) {
      alert(response["message"]);
    }
    return response["result"];
  }

}

const denyPdf = async (formService: FormService, customerFormId: number, reasonDenied: string): Promise<any> => {

  let response = await formService.denyPdf(customerFormId, reasonDenied)
    .toPromise()
    .catch(error => {
      if (error instanceof Object) {
        if (error["ResponseException"] != "") {
          alert(error["ResponseException"]);
        }
      }
      console.log(error);
    });
  if (response instanceof Object) {
    if (response["message"] != "" && response["message"] != undefined) {
      alert(response["message"]);
    }
    return response["result"];
  }
}


const getPdf = async (pdfService: PdfService, fileName: string): Promise<string> => {

  let data = await pdfService.getPdfMaster(fileName)
    .toPromise()
    .catch(error => {
      console.log(error);
      if (error.ResponseException != undefined && error.ResponseException != "") {
        alert(error.ResponseException);
      }
    });

  if (data instanceof Object) {
    // let f = data["result"];
    // let base64 = f.fileContents;
    // var raw = window.atob(base64);
    // var rawLength = raw.length;
    // var array = new Uint8Array(new ArrayBuffer(rawLength));
    // var i = 0;
    // for (i = 0; i < rawLength; i++) {
    //   array[i] = raw.charCodeAt(i);
    // }


    // const blob = new Blob([array], { type: 'application/pdf' });
    // console.log(window.URL.createObjectURL(blob));
    
    return data["result"]
  }

  return null;
}


const getFormsSubmittedList = async (formService: FormService,payload:any): Promise<any> => {

  let response = await formService.getFormsSubmittedByCompany(payload)
    .toPromise()
    .catch(error => {
      // if (error instanceof Object) {
      //   if (error["ResponseException"] != "") {
      //     alert(error["ResponseException"]);
      //   }
      // }
      console.log(error);
    });
  if (response instanceof Object) {
    if (response["message"] != "" && response["message"] != undefined) {
      alert(response["message"]);
    }
    if (response) {
      return response;
    }
    return null;
  }

}

@Component({
  selector: 'app-certificates',
  templateUrl: './certificates.component.html',
  styleUrls: ['./certificates.component.scss']
})
export class CertificatesComponent implements OnInit {
  eCert: string = DeliveryMethod.ECERT;
  paperCert: string = DeliveryMethod.PAPERCERT;

  searchResultListAll: CustomerFormModel [] = [];
  searchResultList: CustomerFormModel [];
    
  //paging
  collectionSize: number = 0;
  page: number = 1;
  pageSize: number = 10;

  //Reason Denied
  customerFormReason: ReasonDeniedModel;
  reasonDenied: string = "";
  
  //search
  searchTerm: string = "";
  searchSubject: Subject<string> = new Subject();

  //sorting
  currentColumn: SortColumn = "";
  currentSortBy: SortDirection = "desc";

  //loading
  approveLoading: boolean = false;

  pdfFile: SafeResourceUrl;

  selectedCertificate: CustomerFormModel;

  sortedField;
  isDescending = true;
  
  public get certificateStatus(): typeof CertificateStatus {
    return CertificateStatus;
  }

  constructor(private formService: FormService,
    private pdfService: PdfService,
    private activeModal: NgbModal,
    public sanitizer: DomSanitizer) {
    }
    
    ngOnInit(): void {
      this.loadData();
      this.searchSubject.pipe(
          debounceTime(500), // Wait for 500ms pause in typing
          distinctUntilChanged() // Ignore if next search term is same as previous
      ).subscribe((searchText) => {
          this.searchTerm = searchText;
          this.loadData();
      });
  }

  onSearchChange(searchText: string) {
    this.page = 1;
    this.searchSubject.next(searchText);
  }

  onSort(event: any) {
    const column = (event.currentTarget || event.target).getAttribute("sortable");
    if (!column) return;
    // this.page = 1; // bring back to page 1
    this.sortedField = column;
    if (this.currentColumn !== column && this.currentColumn !== "") {
      this.currentColumn = column;
      this.currentSortBy = "asc"; // revert to default sort      
    } else {
      if (this.currentColumn == "") { // first sort
        this.currentColumn = column;
        // Set default sort direction based on column type
        if (this.currentColumn == "dateSubmitted" || this.currentColumn == "expirationDate") {
          this.currentSortBy = "desc"; // newest first for dates
        } else {
          this.currentSortBy = "asc";
        }
      } else {
        // Toggle sort direction
        this.currentSortBy = this.currentSortBy == "asc" ? "desc" : "asc";
      }
    }

    this.isDescending = this.currentSortBy == "desc" ? true : false;
    this.page = 1;
    this.loadData();
    // this.searchResultList = sortData(this.searchResultList, column, this.currentSortBy);
  }

  loadData() {
    let payload = {
      page: this.page || 1,
      size: this.pageSize || 10,
      search: this.searchTerm || "",
      sortedField: this.sortedField || "dateSubmitted",
      isDescending: this.isDescending
    }
    getFormsSubmittedList(this.formService,payload).then(data => {
      
      if(data && data['submittedCertificates']){
        this.searchResultListAll = data['submittedCertificates'];
        this.searchResultList = data['submittedCertificates'];
        this.collectionSize = data['totalRecords'];
  
        // var column = 'dateSubmitted'
        // this.page = 1; //bring back to page 1
        // this.searchResultList = sortData(this.searchResultList, column, this.currentSortBy);
  
        // if (this.searchTerm != "") {
        //   this.searchData(this.searchTerm);
        // }
      }else{
        this.searchResultListAll = [];
        this.searchResultList = [];
        this.collectionSize = 0;
      }
    });
  }

  changePage(e) {
    this.page = e;
    this.loadData();
  }
  resetPage() {
      this.page = 1;
      this.loadData();
      // this.pageShipping = 1;
  }

  searchData(_term: string) {
    let term = _term.toLowerCase();
    this.searchTerm = term;
    if (_term == "") {
      this.searchResultList = this.searchResultListAll;
    }
    else {
      this.searchResultList = this.searchResultListAll.filter(data =>
        (data.customerName == null ? "" : data.customerName).toLowerCase().indexOf(term) > -1
        || (data.customerNumber == null ? "" : data.customerNumber).toLowerCase().indexOf(term) > -1
        || (data.stateAbbreviation == null ? "" : data.stateAbbreviation).toLowerCase().indexOf(term) > -1
        || (data.formName == null ? "" : data.formName).toLowerCase().indexOf(term) > -1        
        || (data.campaignName == null ? "" : data.campaignName).toLowerCase().indexOf(term) > -1        
        //|| (data.dateSubmitted == null ? "" : data.dateSubmitted.toDateString()).toLowerCase().indexOf(term) > -1
        || (data.certificateStatus == null ? "" : data.certificateStatus).toLowerCase().indexOf(term) > -1
        || (data.fileName == null ? "" : data.fileName).toLowerCase().indexOf(term) > -1
        || (data.referenceNumber == null ? "" : data.referenceNumber).toLowerCase().indexOf(term) > -1
      );
      this.page = 1;
    }
    this.collectionSize = this.searchResultList.length;
  }

  getPdf(modal, certificate) {
    //this.activeModal.dismissAll();
    getPdf(this.pdfService, certificate.fileName).then(data => {
      if (data != "" && data != undefined) {
        this.selectedCertificate = certificate;
        this.pdfFile = this.sanitizer.bypassSecurityTrustResourceUrl(data);
        this.activeModal.open(modal, { centered: true, size: 'lg', scrollable: true, ariaLabelledBy: 'pdfViewerModalTitle' });
      }
    });
    setTimeout(this.checkModalBody, 1000);
  }

  checkModalBody() {
    //add class modal-open to fix 
    if (document.getElementsByClassName("modal").length !== 0 && document.getElementsByClassName("modal-backdrop").length !== 0) {
      //alert(document.getElementsByClassName("modal-open").length);
      if (document.getElementsByClassName("modal-open").length == 0) {
        document.body.classList.add("modal-open");
      }
    }
  }


  closeModal() {
    this.activeModal.dismissAll();
  }

  approvePdf() {
    this.approveLoading = true;
    approvePdf(this.formService, this.selectedCertificate.customerFormId, this.selectedCertificate.fileName).then(data => {
      if (data != null) {
        this.loadData();
        this.closeModal();
        this.approveLoading = false;
      }
    });
  }

  getReasonDenied(modal) {
    this.reasonDenied = "";
    this.activeModal.open(modal, { centered: true, size: 'sm', scrollable: true, ariaLabelledBy: 'reasonDeniedModalTitle' });
  }

  denyPdf() {
    denyPdf(this.formService, this.selectedCertificate.customerFormId, this.reasonDenied).then(data => {
      if (data != null) {
        this.loadData();
        this.closeModal();
      }
    });
  }
}
